<script lang="ts" setup>
import { computed } from 'vue';
import { Picture } from '~/types';
import Image from '~/components/ui/Image.vue';
import { Icon } from '@iconify/vue';

export interface Item {
  image: Picture;
  title: string;
  button_label?: string;
  url?: string;
}

interface Props {
  item: Item;
}

const props = defineProps<Props>();
const wrapperTag = computed(() => (props.item.url ? 'a' : 'div'));
const attrs = computed(() => (props.item.url ? { href: props.item.url } : undefined));
</script>

<template>
  <component :is="wrapperTag" v-bind="attrs" class="promo-cards-item" :class="{ 'promo-cards-item--link': item.url }">
    <div v-if="item.image" class="promo-cards-item__image-wrapper">
      <Image :image="item.image" class="promo-cards-item__image" />
    </div>

    <div class="promo-cards-item__body">
      <strong v-if="item.title" class="promo-cards-item__title">{{ item.title }}</strong>
      <span v-if="item.button_label" class="promo-cards-item__button">
        {{ item.button_label }}
        <Icon icon="ph:arrow-right" />
      </span>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.promo-cards-item {
  @apply flex items-end aspect-[1/1] max-w-full relative bg-dark overflow-hidden rounded-10;
  @apply xl:rounded-20;

  &__image-wrapper {
    @apply absolute w-full h-full inset-0;
    @apply before:absolute before:w-full before:h-2/3 before:bottom-0 before:z-[1] before:left-0 before:bg-gradient-to-t before:from-dark/70;
  }

  &__image {
    @apply absolute w-full h-full inset-0 object-cover object-center duration-300;
  }

  &__body {
    @apply relative z-[2] text-white flex flex-col w-full;
  }

  &__title {
    @apply font-title py-8 px-6 text-center text-[30px];
    @apply 2xl:text-[40px];
  }

  &__button {
    @apply ml-auto px-5 py-4 bg-light-accent duration-300 rounded-tl-10 overflow-hidden text-dark min-w-[50%] max-w-[60%] font-bold text-[16px] leading-[1.1] items-center flex justify-between gap-4 text-start;
    @apply [&>svg]:w-8 [&>svg]:h-7 [&>svg]:-mr-2;
    @apply xl:rounded-tl-20;
  }

  &--link:hover &__image {
    @apply scale-105;
  }

  &--link:hover &__button {
    @apply bg-accent text-white;
  }
}
</style>
